import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import { BulkVendorMappingRow } from "./BulkVendorMappingRow";
import type { VendorLabelGroup } from "./import-readiness";

interface ImportVendorMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorLabelGroups: VendorLabelGroup[];
  vendors: AdminVendorRow[];
  mappingBusy: boolean;
  onMapExisting: (vendorLabel: string, vendorId: string) => void;
  onMapNew: (vendorLabel: string, contactEmail: string) => void;
}

export function ImportVendorMappingDialog({
  open,
  onOpenChange,
  vendorLabelGroups,
  vendors,
  mappingBusy,
  onMapExisting,
  onMapNew,
}: ImportVendorMappingDialogProps) {
  const { t } = useTranslation();

  if (vendorLabelGroups.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(85vh,900px)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>{t("marketplace.admin.import.vendorMappingModalTitle")}</DialogTitle>
          <DialogDescription>
            {t("marketplace.admin.import.vendorMappingSubtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-5">
          {vendorLabelGroups.map((group) => (
            <BulkVendorMappingRow
              key={`${group.label}-${group.mappedVendorId ?? ""}-${group.pendingVendorContactEmail ?? ""}`}
              group={group}
              vendors={vendors}
              disabled={mappingBusy}
              onMapExisting={(vendorId) => onMapExisting(group.label, vendorId)}
              onMapNew={(contactEmail) => onMapNew(group.label, contactEmail)}
            />
          ))}
        </div>

        <div className="flex justify-end border-t border-border/60 px-6 py-4">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.close", "Close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
